import React, { useMemo, useState, useEffect, useRef } from 'react'
import { connect, useSelector } from 'react-redux'
import { Link, withRouter } from 'react-router-dom'
import { History } from 'history'
import numeral from 'numeral'

import LoaderButton from '../../../components/loaderButton'
import { userAddressPath } from '../../../helpers/linkHelper'
import { formatValue } from '../../../components/inputs/currencyInput'
import ProductsBreakdown from './ProductsBreakdown'
import {
  downPaymentForTotal,
  firstPaymentWindow,
  hasUsableDiscount,
  selectedPrices,
} from '../../../helpers/refiAppHelper'
import {
  calculatePaymentOptions,
  updateProductPackage,
  finalizeProductPackage,
  sendProductPackageConfirmations,
  cleanProductPackageData,
  applyMonthlyMembershipPosition,
  applyFloridaMarkupPosition,
} from '../productPackageActions'
import { refreshFirstPaymentWindow } from '../../products/productActions'
import { selectCoverageProducts } from '../../../reducers/refis'
import { coverageDetailsPreviousStep } from '../refiActions'

import { refreshCoverageProducts } from '../credit/creditApplicationChoice'
import {
  selectCurrentPackageProducts,
  selectProductPackagePaymentOptions,
  selectProductPackage,
  selectCurrentRefiVehicle,
} from '../../../reducers'
import {
  selectCurrentRefiUser,
  selectCurrentProductPackageId,
  selectBusinessConfig,
} from '../../../reducers'
import { User, Vehicle } from '../../../core/types'
import { PaymentFullOption } from './payment/PaymentFullOption'
import { PaymentMontlyOption } from './payment/PaymentMonthlyOption'
import PaymentDetails from './payment/PaymentDetails'
import PaymentCalendar from './payment/PaymentCalendar'
import ApplyFinanceOption from './payment/ApplyFinanceOption'
import { ModalWrapper } from '../../../components/modalWrapper'
import { triggerVinRequiredError, openVehicleSection } from '../refiActions'
import { PaymentMode } from '../../../core/types'

const ADDRESS_NEEDED_HINT = "Add the customer's address first"

export type Callback<T = void> = (...args: any) => T

export interface PaymentOption {
  numberPayment: number
  monthValue: number
  total: number
}

interface PaymentOptionsProps {
  user: User
  productPackageId: number
  updateProductPackage: Callback<Promise<any>>
  applyMonthlyMembershipPosition: Callback<Promise<any>>
  applyFloridaMarkupPosition: Callback<Promise<any>>
  finalizeProductPackage: Callback<Promise<any>>
  sendProductPackageConfirmations: Callback<Promise<any>>
  cleanProductPackageData: Callback<Promise<any>>
  coverageDetailsPreviousStep: Callback
  refreshCoverageProducts: Callback<Promise<any>>
  selectedCoverageProducts: any
  allProducts: any
  calculatePaymentOptions: Callback
  paymentOptions: any
  history: History
  triggerVinRequiredError: Callback
  openVehicleSection: Callback
  vehicle: Vehicle
  refreshFirstPaymentWindow: Callback<Promise<any>>
}

const PaymentOptions = ({
  user,
  productPackageId,
  updateProductPackage,
  finalizeProductPackage,
  sendProductPackageConfirmations,
  cleanProductPackageData,
  coverageDetailsPreviousStep,
  refreshCoverageProducts,
  selectedCoverageProducts,
  allProducts,
  calculatePaymentOptions,
  paymentOptions,
  history,
  triggerVinRequiredError,
  openVehicleSection,
  vehicle,
  applyMonthlyMembershipPosition,
  applyFloridaMarkupPosition,
  refreshFirstPaymentWindow,
}: PaymentOptionsProps) => {
  const [showConfirm, setShowConfirm] = useState(false)
  const today = new Date()

  const businessConfig = useSelector(selectBusinessConfig)
  const productPackage = selectProductPackage(
    useSelector((state: any) => state.productPackages),
    useSelector(selectCurrentProductPackageId)
  )
  const discount = productPackage?.discount
  // Quoting works without an address (CallTools leads arrive without one) but
  // the server refuses to create the agreements, so don't offer the button.
  const addressNeeded = !!productPackage?.addressNeeded
  const hasDiscount = hasUsableDiscount(discount)
  const baseList = selectedPrices(selectedCoverageProducts, allProducts)

  // selectedPrices flattens options down to {name, price}, so the monthly
  // membership flag has to come off the original option.
  const isMonthlyMembership = useMemo(
    () =>
      (selectedCoverageProducts?.selectedOptions || []).some((selected) => {
        const product = allProducts.find(
          (p) => String(p.id) === String(selected.productId)
        )

        return !!product?.options?.find(
          (o) => String(o.id) === String(selected.id)
        )?.monthly_membership
      }),
    [selectedCoverageProducts, allProducts]
  )
  const products = useMemo(
    () =>
      baseList.map((p) => ({
        name: p.name,
        options: p.options.map((opt, idx) => {
          // apply markup/discount only to the base line (first entry)
          let price = parseFloat(opt.price?.toString() || '0')
          // The discount column defaults to {} and {} is truthy, so a package
          // with no discount used to reach this branch with amount undefined:
          // the reduction came out NaN, the price went NaN, and numeral renders
          // NaN as $0.00. It only hit the base line, so a fixed-term package
          // still showed plausible surcharge rows -- but a monthly membership
          // has only that one row, so the card and the Total both read $0.00.
          // Same guard calculateProductPricing already uses.
          if (hasDiscount && idx === 0) {
            const { amount, type } = discount
            const reduction = type === '%' ? (price * amount) / 100 : amount
            price -= reduction
          }
          return { ...opt, price }
        }),
      })),
    [baseList, hasDiscount, discount]
  )

  // Position count is a property of the plan's configuration, carried on the
  // option, so the discount controls need no extra round trip.
  const monthlyMembershipPositionCount = useMemo(
    () =>
      (selectedCoverageProducts?.selectedOptions || []).reduce(
        (count, selected) => {
          const product = allProducts.find(
            (p) => String(p.id) === String(selected.productId)
          )
          const option = product?.options?.find(
            (o) => String(o.id) === String(selected.id)
          )

          return Math.max(count, option?.monthly_membership_position_count || 0)
        },
        0
      ),
    [selectedCoverageProducts, allProducts]
  )

  const monthlyMembershipPosition = productPackage?.monthlyMembershipPosition || 0

  // Florida files its rates, so its markup steps between fixed configured tiers
  // rather than being discounted by an arbitrary amount. The count is carried on
  // the option for the same reason as the monthly membership one: it has to be
  // available before the selection is saved.
  const floridaMarkupPositionCount = useMemo(
    () =>
      (selectedCoverageProducts?.selectedOptions || []).reduce(
        (count, selected) => {
          const product = allProducts.find(
            (p) => String(p.id) === String(selected.productId)
          )
          const option = product?.options?.find(
            (o) => String(o.id) === String(selected.id)
          )

          return Math.max(count, option?.florida_markup_position_count || 0)
        },
        0
      ),
    [selectedCoverageProducts, allProducts]
  )

  const floridaMarkupPosition = productPackage?.floridaMarkupPosition || 0

  // Each click steps to the next configured position, a lower markup and so a
  // lower monthly price. Reset returns to the initial price. Repricing changes
  // the payment options, so they are refetched.
  const stepMonthlyMembershipPosition = (position: number) => {
    applyMonthlyMembershipPosition(productPackageId, position).then(() => {
      if (user?.id) {
        calculatePaymentOptions(0, productPackageId, user.id, 'ensurety')
      }
    })
  }

  // Set by the down payment input and the provider step, so a Florida
  // Discount/Reset can refetch the payment options with what the agent sees.
  const downPaymentEditedRef = useRef(false)
  const providerRef = useRef<string | null>(null)
  const refetchAfterStepRef = useRef(false)

  // Repricing changes the payment options, so they are refetched. Unlike the
  // monthly membership step, the down payment matters here: the endpoint returns
  // (total - down) / count. The server scales only the package's *saved* down
  // payment, which is empty until the deal is saved, so its reply carries 0 and
  // every option showed the whole price over the term (blinker-legacy#164). The
  // refetch waits for the new total instead and uses the on-screen down payment,
  // recomputed for that total by the effect below.
  //
  // The flag is raised before the request: the action dispatches the repriced
  // package before its promise resolves, so the new total can arrive first.
  const stepFloridaMarkupPosition = (position: number) => {
    refetchAfterStepRef.current = true
    applyFloridaMarkupPosition(productPackageId, position).catch(() => {
      // The action already showed the error; the total did not change.
      refetchAfterStepRef.current = false
    })
  }

  // A package is one or the other: monthly membership prices from the plan's own
  // margins, Florida from the organization's markup tiers. Both step through
  // fixed positions, so they share one control.
  const monthlyMembershipLadder =
    isMonthlyMembership && monthlyMembershipPositionCount > 1
      ? {
          position: monthlyMembershipPosition,
          count: monthlyMembershipPositionCount,
          step: stepMonthlyMembershipPosition,
          discountTestId: 'm2m-discount-button',
          resetTestId: 'm2m-reset-discount-button',
        }
      : null

  const floridaLadder =
    floridaMarkupPositionCount > 1
      ? {
          position: floridaMarkupPosition,
          count: floridaMarkupPositionCount,
          step: stepFloridaMarkupPosition,
          discountTestId: 'florida-discount-button',
          resetTestId: 'florida-reset-discount-button',
        }
      : null

  const discountLadder = monthlyMembershipLadder || floridaLadder

  const [paymentMode, setPaymentMode] = useState<PaymentMode>(
    isMonthlyMembership ? 'monthly' : 'refinance'
  )

  // A monthly membership has no pay-in-full or refinance path, so the mode is
  // forced rather than chosen. Runs on late-arriving product data too.
  useEffect(() => {
    if (isMonthlyMembership) {
      setPaymentMode('monthly')
    }
  }, [isMonthlyMembership])
  const paymentFlags = useMemo(()=>({
    isFull: paymentMode === 'full',
    isMonthly: paymentMode === 'monthly',
    isRefinance: paymentMode === 'refinance'
  }), [paymentMode])
  const [total, setTotal] = useState(0)
  const [numberPaymentOption, setNumberPaymentOption] = useState(0)
  const [downPayment, setDownPayment] = useState('')
  const paymentDetails = paymentOptions.data.length > 0
  const [isPurchaseLoading, setIsPurchaseLoading] = useState(false)
  const [isBackBtnLoading, setIsBackBtnLoading] = useState(false)
  const [minDate, setMinDate] = useState(new Date(today))
  const [maxDate, setMaxDate] = useState(new Date(today))
  const [date, setDate] = useState(minDate)
  const [calendarDate, setCalendarDate] = useState(minDate)

  // The window's dates come from the server, not this browser's clock
  // (blinker-legacy#47); see firstPaymentWindow.
  useEffect(() => {
    const { minDate: minD, maxDate: maxD } = firstPaymentWindow(productPackage)
    setMinDate(minD)
    setMaxDate(maxD)
    setCalendarDate(minD)
    setDate(minD)
  }, [
    productPackage?.firstPaymentMinDays,
    productPackage?.firstPaymentMaxDays,
    productPackage?.firstPaymentMinDate,
    productPackage?.firstPaymentMaxDate,
  ])

  // The cached window may be days old; see refreshFirstPaymentWindow.
  useEffect(() => {
    if (productPackageId) refreshFirstPaymentWindow(productPackageId)
  }, [productPackageId])

  
  
  const calendarMonth = calendarDate.toLocaleString('default', {
    month: 'long',
  })
  const calendarYear = calendarDate.getFullYear()
  const [isDownPaymentNextButtonDisabled, setIsDownPaymentNextButtonDisabled] =
    useState(true)

  // A monthly membership's down payment is the full price, so the 75% cap that
  // applies to financed products would reject it.
  const paymentLimits = { max: isMonthlyMembership ? total : total * 0.75 }

  const {
    down_payment_percent_default: defaultPct = 0,
    down_payment_percent_min: minPct = 0,
  } = businessConfig.loan || {}

  useEffect(() => {
    if (paymentFlags.isMonthly && total > 0) {
      const nextDownPayment = downPaymentForTotal({
        total,
        isMonthlyMembership,
        paidDownPayment:
          productPackage?.status?.code === 'payment_succeeded'
            ? Number(productPackage?.down_payment)
            : null,
        currentDownPayment: numeral(downPayment)._value,
        agentEdited: downPaymentEditedRef.current,
        defaultPct,
        minPct,
        maxRatio: 0.75,
      })
      setDownPayment(nextDownPayment.toFixed(2))
      setIsDownPaymentNextButtonDisabled(false)

      if (refetchAfterStepRef.current && user?.id) {
        refetchAfterStepRef.current = false
        calculatePaymentOptions(
          Number(nextDownPayment.toFixed(2)),
          productPackageId,
          user.id,
          providerRef.current || 'ensurety'
        )
      }
    }
  }, [paymentMode, total, defaultPct, isMonthlyMembership])

  const onCurrencyChange = (value: string) => {
    downPaymentEditedRef.current = true
    const formatted = formatValue(value)

    const amt = numeral(formatted)._value ?? 0
    const minVal = (total * minPct) / 100

    setDownPayment(formatted)
    setIsDownPaymentNextButtonDisabled(amt < minVal || amt > paymentLimits.max)
  }


  const onEnterPress = (paymentPlanProvider: string) => {
    providerRef.current = paymentPlanProvider
    calculatePaymentOptions(
      numeral(downPayment)._value,
      productPackageId,
      user.id,
      paymentPlanProvider
    )
  }

  // The provider step is what normally triggers the payment-options fetch, and a
  // monthly membership skips it, so fetch directly. The down payment is derived
  // server-side for M2M, so the value sent here is not used.
  useEffect(() => {
    if (isMonthlyMembership && total > 0 && productPackageId && user?.id) {
      calculatePaymentOptions(0, productPackageId, user.id, 'ensurety')
    }
  }, [isMonthlyMembership, total, productPackageId, user?.id])

  // A monthly membership returns exactly one option and it is preselected --
  // there is no payment count for the agent to choose.
  useEffect(() => {
    if (isMonthlyMembership && paymentOptions.data.length === 1) {
      setNumberPaymentOption(paymentOptions.data[0].numberPayment)
    }
  }, [isMonthlyMembership, paymentOptions.data])

  const purchaseButtonDisabled = useMemo(() => {
    if (paymentFlags.isFull) return false
    return !downPayment || !numberPaymentOption || !date
  }, [downPayment, numberPaymentOption, date, paymentMode])

  const toYMD = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate()
    ).padStart(2, '0')}`

  const finalizePurchaseParams = useMemo(() => {
    const downPaymentValue = numeral(downPayment)
    return {
      user_id: user.id,
      id: productPackageId,
      down_payment: paymentFlags.isFull ? null : 100 * downPaymentValue._value,
      payment_count: paymentFlags.isFull ? 1 : numberPaymentOption,
      first_payment_date: paymentFlags.isFull ? null : toYMD(date),
      send: true,
    }
  }, [paymentMode, downPayment, numberPaymentOption, date, today])

  const selectProduct = async () => {
    setIsPurchaseLoading(true)
    return await updateProductPackage(finalizePurchaseParams)
  }

  const sendConfirmations = async () => {
    setIsPurchaseLoading(true)
    return await sendProductPackageConfirmations(finalizePurchaseParams)
  }

  const finalizePurchase = async () => {
    setIsPurchaseLoading(true)

    if (productPackage.status?.code === 'product_remitted_error') {
      await cleanProductPackageData(productPackageId)
    }

    await finalizeProductPackage(finalizePurchaseParams)

    // Continue to EFS
    history.push(`/refiApp/payment?packageId=${productPackage.id}`)
  }

  const handleBack = async () => {
    setIsBackBtnLoading(true)
    try {
      await refreshCoverageProducts()
    } catch (_err) {
      // refreshCoverageProducts already shows a notification on failure;
      // proceed regardless so the user is not stuck
    } finally {
      setIsBackBtnLoading(false)
    }
    coverageDetailsPreviousStep()
  }

  const onSaveAndSend = async () => {
    // if no VIN has been entered, then prompt the user to validate it now or later
    if (!vehicle.vin) {
      setShowConfirm(true)
    } else {
      try {
        await finalizePurchase()
      } catch (_err) {
        // finalizeProductPackage already showed the server's reason (e.g.
        // address_required); stay on this step with the button usable again.
        setIsPurchaseLoading(false)
        return
      }

      // Continue to user profile
      history.push(`/users/${user.id}`)
    }
  }

  return (
    <>
      {' '}
      {/* Confirmation modal for paid/booked => VIN validation */}
      {showConfirm && (
        <ModalWrapper
          title="Validate Pricing?"
          hideModal={() => setShowConfirm(false)}
          xclose={true}
          width="400px"
          height="200px"
        >
          <div className="modal__buttons">
            <button
              className="button button-drive-america"
              onClick={() => {
                selectProduct()
                triggerVinRequiredError()
                openVehicleSection()
                setShowConfirm(false)
              }}
            >
              Validate Pricing
            </button>
            <button
              className="button button-drive-america"
              onClick={async () => {
                setShowConfirm(false)
                await selectProduct()
                await sendConfirmations()

                // Continue to user profile
                history.push(`/users/${user.id}`)
              }}
            >
              Ask Me Later
            </button>
          </div>
        </ModalWrapper>
      )}
      <div className="refi-payment-options__main-container">
        {products ? (
          <div className="refi-payment-options__container">
            <ProductsBreakdown
              products={products}
              getTotal={(total) => setTotal(total)}
              downPayment={numeral(productPackage.down_payment)._value}
              basePrice={numeral(productPackage.basePrice)._value}
              validationDiscountMax={numeral(productPackage.validationDiscountMax)._value}
              status={productPackage.status}
            />
          </div>
        ) : null}
        {!isMonthlyMembership && productPackage.status?.code !== 'payment_succeeded' && productPackage.status?.code !== 'product_remitted_error' && (
              <>
                <ApplyFinanceOption
                  isChecked={paymentFlags.isRefinance}
                  productPackage={productPackage}
                  setPaymentOption={setPaymentMode}
                  total={total}
                />

                <PaymentFullOption
                  isChecked={paymentFlags.isFull}
                  setPaymentOption={setPaymentMode}
                  total={total}
                />
              </>
        )}

        {total > 0 ? (
          <PaymentMontlyOption
            isChecked={paymentFlags.isMonthly}
            setPaymentOption={setPaymentMode}
          >
            {paymentFlags.isMonthly && (
              <div className="refi-payment-options__down-payment-container">
                {paymentOptions.loading ? (
                  <>Loading Payment Details...</>
                ) : (
                  <PaymentDetails
                    showPaymentDetails={paymentDetails}
                    downPayment={downPayment}
                    numberPayment={numberPaymentOption}
                    onCurrencyChange={onCurrencyChange}
                    onEnterPress={onEnterPress}
                    setNumberPayment={setNumberPaymentOption}
                    paymentOptions={paymentOptions.data}
                    isDownPaymentNextButtonDisabled={
                      isDownPaymentNextButtonDisabled
                    }
                    paymentLimits={paymentLimits}
                    productPackage={productPackage}
                    isMonthlyMembership={isMonthlyMembership}
                  >
                    <PaymentCalendar
                      date={date}
                      setDate={setDate}
                      calendarYear={calendarYear}
                      setCalendarDate={setCalendarDate}
                      minDate={minDate}
                      maxDate={maxDate}
                      calendarMonth={calendarMonth}
                    />
                  </PaymentDetails>
                )}
              </div>
            )}
          </PaymentMontlyOption>
        ) : null}

        <LoaderButton
          type="button"
          className="button button-drive-america button--inline"
          onClick={handleBack}
          isLoading={isBackBtnLoading}
          disabled={isBackBtnLoading}
        >
          Back
        </LoaderButton>

        <LoaderButton
          type="submit"
          className="button button-drive-america"
          onClick={onSaveAndSend}
          disabled={purchaseButtonDisabled || addressNeeded}
          isLoading={isPurchaseLoading}
          title={addressNeeded ? ADDRESS_NEEDED_HINT : undefined}
        >
          Save and Send
        </LoaderButton>

        {addressNeeded && (
          <p
            className="text-error"
            style={{ marginTop: '8px' }}
            data-testid="address-needed-hint"
          >
            {ADDRESS_NEEDED_HINT}.{' '}
            <Link to={userAddressPath(user)}>Add address</Link>
          </p>
        )}

        {discountLadder && (
          <>
            <LoaderButton
              type="button"
              className="button button-drive-america button--inline"
              onClick={() => discountLadder.step(discountLadder.position + 1)}
              disabled={discountLadder.position >= discountLadder.count - 1}
              data-testid={discountLadder.discountTestId}
            >
              Discount
            </LoaderButton>
            <LoaderButton
              type="button"
              className="button button-drive-america button--inline"
              onClick={() => discountLadder.step(0)}
              disabled={discountLadder.position === 0}
              data-testid={discountLadder.resetTestId}
            >
              Reset
            </LoaderButton>
            <span className="refi-payment-options__section-title">
              {discountLadder.position === 0
                ? 'Initial price'
                : `Discount ${discountLadder.position} of ${
                    discountLadder.count - 1
                  }`}
            </span>
          </>
        )}
      </div>
    </>
  )
}

export const mapStateToProps = (state: any) => ({
  user: selectCurrentRefiUser(state),
  productPackageId: selectCurrentProductPackageId(state),
  selectedCoverageProducts: selectCoverageProducts(state),
  allProducts: selectCurrentPackageProducts(state),
  paymentOptions: selectProductPackagePaymentOptions(state),
  vehicle: selectCurrentRefiVehicle(state),
})

export default withRouter(
  connect(mapStateToProps, {
    updateProductPackage,
    finalizeProductPackage,
    sendProductPackageConfirmations,
    cleanProductPackageData,
    coverageDetailsPreviousStep,
    refreshCoverageProducts,
    calculatePaymentOptions,
    triggerVinRequiredError,
    openVehicleSection,
    applyMonthlyMembershipPosition,
    applyFloridaMarkupPosition,
    refreshFirstPaymentWindow,
  })(PaymentOptions)
)
